import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, ConfirmPopup } from "@commonsku/styles";
import { BulkActionConfigBase } from "../index";
import { useDispatch } from "react-redux";
import { deleteMultipleCreditMemos } from "../../../../redux/credit_memos";
import { useReportContext } from "../../report_context";
import { useHasCapabilities } from "../../../../hooks";
import { createExportToAccountingButton } from "../common/bulk-actions";
import { oauth } from "../../../../utils";

export function prepareLabel<
  T extends Record<string, unknown> = Record<string, unknown>
>(selected: T[]): React.ReactNode {
  if (selected.length === 0) {
    return "Select one or more credit memos";
  }
  if (selected.length === 1) {
    return "1 credit memo selected";
  }
  return `${selected.length} credit memos selected`;
}

export function DeleteCreditMemosButton<T extends Record<string, unknown> = Record<string, unknown>>(
  {
    selected,
    setExecuting,
    disabled,
  }: {
    selected: T[],
    setExecuting: (executing?: boolean) => void,
    disabled?: boolean,
  }
) {
  const canDelete = useHasCapabilities(["DELETE-CREDIT-MEMO"]);
  const [showConfirm, setShowConfirm] = useState(false);
  const dispatch = useDispatch();
  const { fetchReport } = useReportContext();

  const credit_memo_ids = useMemo(
    () => selected.map((row: any) => row.form_number.credit_memo_id),
    [selected]
  );

  const handleDelete = useCallback(async () => {
    setExecuting(true);
    try {
      await dispatch(deleteMultipleCreditMemos(credit_memo_ids));
      fetchReport();
    } catch (e) {
      toast.error("Unable to delete credit memos");
    } finally {
      setExecuting(false);
      setShowConfirm(false);
    }
  }, [setExecuting, credit_memo_ids, dispatch, fetchReport]);

  if (!canDelete) {
    return null;
  }

  return (
    <>
      {showConfirm && (
        <ConfirmPopup
          confirmText="Are you sure you want to delete?  Unpaid commissions will be recalculated."
          onClose={() => setShowConfirm(false)}
          onDelete={handleDelete}
        />
      )}
      <Button
        disabled={disabled}
        onClick={() => setShowConfirm(true)}
        variant="error"
      >
        Delete
      </Button>
    </>
  );
}

export function UnExportCreditMemosButton<T extends Record<string, unknown> = Record<string, unknown>>(
  {
    selected,
    setExecuting,
    disabled,
  }: {
    selected: T[],
    setExecuting: (executing?: boolean) => void,
    disabled?: boolean,
  }
) {
  const canDelete = useHasCapabilities(["DELETE-CREDIT-MEMO"]);
  const { fetchReport } = useReportContext();
  const credit_memo_ids = useMemo(
    () => selected.map((row: any) => row.form_number.credit_memo_id),
    [selected]
  );

  // Check if any selected credit memos are exported
  const hasExported = useMemo(
    () => selected.some((row: any) => row.date_exported?.date_exported),
    [selected]
  );

  const handleClick = useCallback(async () => {
    if (credit_memo_ids.length === 1) {
      // eslint-disable-next-line no-restricted-globals
      if (!confirm("Are you sure to want to un-export this credit memo?")) {
        return;
      }
    } else {
      // eslint-disable-next-line no-restricted-globals
      if (!confirm("Are you sure to want to un-export these credit memos?")) {
        return;
      }
    }
    setExecuting(true);
    try {
      await oauth("POST", "credit-memo", { action: "unexport", ids: credit_memo_ids });
      if (credit_memo_ids.length === 1) {
        toast.success("1 credit memo unexported");
      } else {
        toast.success(`${credit_memo_ids.length} credit memos unexported`);
      }
      await fetchReport();
    } catch (e) {
      toast.error("Unable to un-export credit memos");
    } finally {
      setExecuting(false);
    }
  }, [setExecuting, credit_memo_ids, fetchReport]);

  if (!canDelete) {
    return null;
  }

  return (
    <Button
      disabled={disabled || !hasExported}
      onClick={handleClick}
    >
      Un-Export
    </Button>
  );
}

const ExportToAccountingButton = createExportToAccountingButton(
  "CREDIT-MEMO",
  (row: any) => row.form_number.credit_memo_id,
  false,
  (row: any) => !!row.date_exported.date_exported,
);

export const BulkActions: BulkActionConfigBase = {
  prepareLabel,
  actions: [
    DeleteCreditMemosButton,
    UnExportCreditMemosButton,
    ExportToAccountingButton,
  ]
};
